import { Status, TextLink } from '@mm/components';
import { CtmAdIndexModel } from '@mm/state/model/CtmAdModel';
import { UICampaignItem } from '@mm/state/reducers/campaignsOverview';
import { activeFacebookPagePlanInfoSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { ThreeDotMenu } from '../../../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import { formatDateTime } from '../../../../../../../common/helpers/formatDateTime';
import { trackWebAppGAEvent } from '../../../../../../../common/helpers/plugins/website-google-analytics';
import { useDeleteCampaign } from '../../use-delete-campaign';
import { EntityRowBaseTemplate } from '../EntityRowBaseTemplate';
import { CampaignBaseInfo, Kind } from '../SharedCells';
import { BotBuilderRoutes } from '../../../../../../../routes/bot-builder-routes';

export type AdConfigStatusType =
  | 'Draft'
  | 'Published'
  | 'Publishing'
  | 'Updating'
  | 'Paused';

export const getAdConfigStatus = (
  {
    draft,
    publishingProcess,
  }: {
    draft: boolean;
    publishingProcess: boolean;
  },
  isFree?: boolean,
): AdConfigStatusType => {
  if (draft && !publishingProcess) {
    return 'Draft';
  }
  if (draft) {
    return 'Publishing';
  }
  if (!draft && !publishingProcess) {
    return isFree ? 'Paused' : 'Published';
  }
  return 'Updating';
};

export const getSyncedAdStatusText = (
  {
    adAccountId,
    draft,
  }: {
    draft: boolean | null;
    adAccountId: string | null;
  },
  isFree?: boolean,
) => {
  if (adAccountId === null) {
    return 'Connected Manually' + (isFree ? ' (Paused)' : '');
  }
  if (draft) {
    return 'Draft';
  }
  if (isFree) {
    return 'Paused';
  }

  return 'Synced';
};

const isContinuousAd = (ad: CtmAdIndexModel) =>
  ad.adKind === 'new_messenger_ad' && !ad.endDate;

export const CtmAdRow = ({ item }: { item: UICampaignItem }) => {
  const { history } = useRouter();
  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const adName = item.name;
  const campaignName = item.campaignName;
  const bottomText = campaignName && `Campaign: ${campaignName}`;

  const isContinuous = isContinuousAd(item as CtmAdIndexModel);
  const isFree = useSelector(activeFacebookPagePlanInfoSelector).info
    ?.isFreeOrSpecialPlan;

  let startDate = 'Start date: ';
  if (isContinuous) {
    startDate += 'run continuously';
  }
  if (item.startDate && item.endDate) {
    startDate += formatDateTime(item.startDate);
  }

  const status =
    item.adKind === 'synced_messenger_ad'
      ? getSyncedAdStatusText(item as CtmAdIndexModel, isFree)
      : getAdConfigStatus(item as CtmAdIndexModel, isFree);

  const navigateToEditRoute = () => {
    if (activeChatbotId && item.id) {
      history.push(
        BotBuilderRoutes.ctmAdsEdit.getLink({
          chatbotId: activeChatbotId,
          adId: item.id as number,
        }),
      );
    }
  };

  const onRowClick = () => {
    trackWebAppGAEvent('click', {
      category: 'campaigns_table',
      label: 'table_item_new_ads',
    });
    navigateToEditRoute();
  };

  const { deleteCampaign } = useDeleteCampaign();
  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'edit':
        trackWebAppGAEvent('click', {
          category: 'campaigns_table',
          label: 'edit_button_new_ads',
        });
        return navigateToEditRoute();
      case 'delete':
        return deleteCampaign(item);
      default:
        break;
    }
  };

  const elements = [
    { name: 'Edit', value: 'edit' },
    { name: 'Delete', value: 'delete' },
  ];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const renderStartDate = () =>
    item.adKind === 'synced_messenger_ad' ? (
      <span />
    ) : (
      <TextLink>{startDate}</TextLink>
    );

  const renderAdType = () => (
    <TextLink>
      {item.adKind === 'synced_messenger_ad'
        ? 'Created in FB'
        : 'Created in MM'}
    </TextLink>
  );

  return (
    <EntityRowBaseTemplate
      className="starters-table__data-row starters-table__data-row--large"
      onClick={onRowClick}
      inactive={status === 'Draft'}
    >
      <CampaignBaseInfo headingText={adName} bottomText={bottomText} />

      <Kind kind="ctm_ads" />

      {renderAdType()}

      <TextLink>
        {'Last Updated: ' + formatDateTime((item as CtmAdIndexModel).updatedAt)}
      </TextLink>
      {renderStartDate()}
      <Status status={status} />

      <span />
      {/*  <CountWithTitle count="?" text="Impressions" />
      <CountWithTitle count="?" text="Sent" />
      <CountWithTitle count="?" text="Responded" />*/}

      <ThreeDotMenu
        className="shared_cells__menu"
        onClick={onMenuClick}
        elements={elements}
        selectItem={onSelectItem}
      />
    </EntityRowBaseTemplate>
  );
};
